import { useState } from "react";
import {
  getAdvanceNotes,
  createAdvanceNote,
  updateAdvanceNote,
  getAdvanceNoteById,
  deleteAdvanceNote,
} from "../../../Services/billsService"
import { getAllUsers } from "../../../Services/userService";
import { fetchCategories } from "../../../Services/billsService";
import { getSites } from "../../../Services/siteServices";
import toast from "react-hot-toast";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";
import { TOAST_MESSAGES } from "../AdvanceNote/constants";
import { getProjects } from "../../../Services/projectServices";

export const useAdvanceNote = () => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    user: "",
    site: "",
    category: "",
    site__project: "", 
    status: ""
  });

  const onPageChange = (newPage) => setPage(newPage);
  const onPageSizeChange = (newSize) => {
    setRowsPerPage(newSize);
    setPage(1);
  };

  const fetchAdvanceNotes = async (params) => {
    setLoading(true);
    let data = [];
    try {
      const response = await getAdvanceNotes({
        ...filters,
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage,
        ...params
      });

      if (response.status === 200) {
        const results = response.data?.results ?? response.data;
        setTotalPages(
          Math.max(1, Math.ceil((response.data?.count || 0) / rowsPerPage))
        );
        data = results;
      } else {
        toast.error(
          extractErrorMessage(response) || TOAST_MESSAGES.ADV_NOTES_FETCH_FAILED
        );
      }
    } catch (error) {
      toast.error(
        extractErrorMessage(error) || TOAST_MESSAGES.ADV_NOTES_FETCH_ERROR
      );
    } finally {
      setLoading(false);
      return data;
    }
  };

  const createAdvanceNoteData = async (payload) => {
    setLoading(true);
    let data = null;
    try {
      const response = await createAdvanceNote(payload);
      if (response.status === 201) {
        toast.success(TOAST_MESSAGES.ADV_NOTE_CREATE_SUCCESS);
        data = response.data;
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.ADV_NOTE_CREATE_ERROR;
        toast.error(msg);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || TOAST_MESSAGES.ADV_NOTE_CREATE_ERROR);
    } finally {
      setLoading(false);
      return data;
    }
  };

  const editAdvanceNoteData = async (id, payload) => {
    setLoading(true);
    let data = null;
    try {
      const response = await updateAdvanceNote(id, payload);
      if (response.status === 200) {
        toast.success(TOAST_MESSAGES.ADV_NOTE_UPDATE_SUCCESS);
        data = response.data;
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.ADV_NOTE_UPDATE_ERROR;
        toast.error(msg);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || TOAST_MESSAGES.ADV_NOTE_UPDATE_ERROR);
    } finally {
      setLoading(false);
      return data;
    }
  };

  const fetchAdvanceNoteById = async (id) => {
    setLoading(true);
    let data = null;
    try {
      const response = await getAdvanceNoteById(id);
      if (response.status === 200) {
        data = response.data;
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.ADV_NOTE_FETCH_ERROR;
        toast.error(msg);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || TOAST_MESSAGES.ADV_NOTE_FETCH_ERROR);
    } finally {
      setLoading(false);
      return data;
    }
  };

  const deleteAdvanceNoteData = async (id) => {
    setLoading(true);
    let success = false;
    try {
      const response = await deleteAdvanceNote(id);
      if (response.status === 204 || response.status === 200) {
        toast.success(TOAST_MESSAGES.ADV_NOTE_DELETE_SUCCESS);
        success = true;
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.ADV_NOTE_DELETE_ERROR;
        toast.error(msg);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || TOAST_MESSAGES.ADV_NOTE_DELETE_ERROR);
    } finally {
      setLoading(false);
      return success;
    }
  };

  const fetchUsers = async (params) => {
    let data = [];
    try {
      const res = await getAllUsers({ limit: 10, ...params });
      if (res.status === 200) {
        data = res.data?.results ?? res.data;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.ERROR_USERS_FETCH;
        toast.error(msg);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err) || TOAST_MESSAGES.ERROR_USERS_FETCH);
    }
    return data;
  };

  const fetchSites = async (params) => {
    let data = [];
    try {
      const res = await getSites({ limit: 10, ...params });
      if (res.status === 200) {
        data = res.data?.results ?? res.data;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.ERROR_SITES_FETCH;
        toast.error(msg);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err) || TOAST_MESSAGES.ERROR_SITES_FETCH);
    }
    return data;
  };

  const fetchCategoriesList = async (params) => {
    let data = [];
    try {
      const res = await fetchCategories({ limit: 10, ...params });
      if (res.status === 200) {
        data = res.data?.results ?? res.data;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.ERROR_CATEGORIES_FETCH;
        toast.error(msg);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err) || TOAST_MESSAGES.ERROR_CATEGORIES_FETCH);
    }
    return data;
  };

    const fetchProjectSites = async (params) => {
     let data = [];
     try {
       const res = await getProjects({ limit: 10, ...params });
       if (res.status === 200) {
         data = res.data?.results ?? res.data;
       } else {
         const msg =
           extractErrorMessage(res) || TOAST_MESSAGES.ERROR_FETCHING_PROJECTS;
         toast.error(msg);
       }
     } catch (err) {
       toast.error(
         extractErrorMessage(err) || TOAST_MESSAGES.ERROR_FETCHING_PROJECTS
       );
     }
     return data;
    };

  return {
    loading,
    page,
    rowsPerPage,
    totalPages,
    filters,
    setFilters,
    onPageChange,
    onPageSizeChange,
    fetchAdvanceNotes,
    createAdvanceNoteData,
    editAdvanceNoteData,
    fetchAdvanceNoteById,
    deleteAdvanceNoteData,
    setPage,
    setRowsPerPage,
    setTotalPages,
    fetchUsers,
    fetchSites,
    fetchCategoriesList,
    fetchProjectSites,
  };
};
